import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Avatar,
  Badge,
  Tabs,
  Tab,
  Divider,
  Alert,
  Snackbar,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Rating,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Reply as ReplyIcon,
  Assignment as AssignmentIcon,
  Visibility as ViewIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Pause as PauseIcon,
  Cancel as CancelIcon,
  ContentCopy as DuplicateIcon,
  Email as EmailIcon,
  Support as SupportIcon,
  ContactMail as ContactIcon,
  Campaign as CampaignIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Message as MessageIcon,
  Star as StarIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import api from '../services/api';
import { toast } from 'react-toastify';

const CommunicationSupport = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Contact form states
  const [contacts, setContacts] = useState([]);
  const [contactStats, setContactStats] = useState({});
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  
  // Ticket states
  const [tickets, setTickets] = useState([]);
  const [ticketStats, setTicketStats] = useState({});
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageText, setMessageText] = useState('');
  
  // Email campaign states
  const [campaigns, setCampaigns] = useState([]);
  const [campaignStats, setCampaignStats] = useState({});
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');

  const statusColors = {
    new: 'warning',
    read: 'info',
    replied: 'success',
    closed: 'default',
    open: 'error',
    in_progress: 'warning',
    pending_customer: 'info',
    resolved: 'success',
    draft: 'default',
    scheduled: 'info',
    sending: 'warning',
    sent: 'success',
    paused: 'warning',
    cancelled: 'error'
  };

  const priorityColors = {
    low: 'success',
    medium: 'warning',
    high: 'error',
    urgent: 'error'
  };

  useEffect(() => {
    fetchData();
  }, [currentTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      if (currentTab === 0) {
        await Promise.all([
          fetchContacts(),
          fetchContactStats()
        ]);
      } else if (currentTab === 1) {
        await Promise.all([
          fetchTickets(),
          fetchTicketStats()
        ]);
      } else if (currentTab === 2) {
        await Promise.all([
          fetchCampaigns(),
          fetchCampaignStats()
        ]);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await api.get('/contacts');
      setContacts(response.data.contacts);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  };

  const fetchContactStats = async () => {
    try {
      const response = await api.get('/contacts/stats');
      setContactStats(response.data);
    } catch (error) {
      console.error('Failed to fetch contact stats:', error);
    }
  };

  const fetchTickets = async () => {
    try {
      const response = await api.get('/tickets');
      setTickets(response.data.tickets);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    }
  };

  const fetchTicketStats = async () => {
    try {
      const response = await api.get('/tickets/stats');
      setTicketStats(response.data);
    } catch (error) {
      console.error('Failed to fetch ticket stats:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await api.get('/email-campaigns');
      setCampaigns(response.data.campaigns);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  };

  const fetchCampaignStats = async () => {
    try {
      const response = await api.get('/email-campaigns/stats');
      setCampaignStats(response.data);
    } catch (error) {
      console.error('Failed to fetch campaign stats:', error);
    }
  };

  const handleReplyToContact = async () => {
    try {
      await api.post(`/contacts/${selectedContact._id}/reply`, {
        message: replyText
      });
      toast.success('Reply sent successfully');
      setReplyDialogOpen(false);
      setReplyText('');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  const handleAddTicketMessage = async () => {
    try {
      await api.post(`/tickets/${selectedTicket._id}/message`, {
        message: messageText
      });
      toast.success('Message added successfully');
      setMessageDialogOpen(false);
      setMessageText('');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to add message');
    }
  };

  const handleScheduleCampaign = async () => {
    try {
      await api.post(`/email-campaigns/${selectedCampaign._id}/schedule`, {
        scheduledAt: scheduleDate
      });
      toast.success('Campaign scheduled successfully');
      setScheduleDialogOpen(false);
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to schedule campaign');
    }
  };

  const contactColumns = [
    {
      field: 'name',
      headerName: 'Contact',
      width: 200,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
            {params.row.name?.charAt(0) || 'C'}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {params.row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.email}
            </Typography>
          </Box>
        </Box>
      )
    },
    {
      field: 'subject',
      headerName: 'Subject',
      width: 250,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.row.subject}
        </Typography>
      )
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.category}
          size="small"
          color="primary"
          variant="outlined"
        />
      )
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.priority}
          size="small"
          color={priorityColors[params.row.priority]}
        />
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.status}
          size="small"
          color={statusColors[params.row.status]}
        />
      )
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.row.createdAt).toLocaleDateString("en-IN", {
            timeZone: "Asia/Kolkata"
          })}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedContact(params.row);
                setContactDialogOpen(true);
              }}
            >
              <ViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reply">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedContact(params.row);
                setReplyDialogOpen(true);
              }}
            >
              <ReplyIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const ticketColumns = [
    {
      field: 'ticketNumber',
      headerName: 'Ticket #',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium" color="primary">
          {params.row.ticketNumber}
        </Typography>
      )
    },
    {
      field: 'customerName',
      headerName: 'Customer',
      width: 180,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
            {params.row.customerName?.charAt(0) || 'C'}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {params.row.customerName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.customerEmail}
            </Typography>
          </Box>
        </Box>
      )
    },
    {
      field: 'subject',
      headerName: 'Subject',
      width: 250,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.row.subject}
        </Typography>
      )
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.priority}
          size="small"
          color={priorityColors[params.row.priority]}
        />
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.status}
          size="small"
          color={statusColors[params.row.status]}
        />
      )
    },
    {
      field: 'assignedTo',
      headerName: 'Assigned To',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.assignedTo?.name || 'Unassigned'}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedTicket(params.row);
                setTicketDialogOpen(true);
              }}
            >
              <ViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Message">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedTicket(params.row);
                setMessageDialogOpen(true);
              }}
            >
              <MessageIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const campaignColumns = [
    {
      field: 'name',
      headerName: 'Campaign Name',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.row.name}
        </Typography>
      )
    },
    {
      field: 'subject',
      headerName: 'Subject',
      width: 250,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.row.subject}
        </Typography>
      )
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.type}
          size="small"
          color="primary"
          variant="outlined"
        />
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.status}
          size="small"
          color={statusColors[params.row.status]}
        />
      )
    },
    {
      field: 'statistics',
      headerName: 'Recipients',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.statistics?.totalRecipients || 0}
        </Typography>
      )
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.row.createdAt).toLocaleDateString("en-IN", {
            timeZone: "Asia/Kolkata"
          })}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedCampaign(params.row);
                setCampaignDialogOpen(true);
              }}
            >
              <ViewIcon />
            </IconButton>
          </Tooltip>
          {params.row.status === 'draft' && (
            <Tooltip title="Schedule">
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedCampaign(params.row);
                  setScheduleDialogOpen(true);
                }}
              >
                <ScheduleIcon />
              </IconButton>
            </Tooltip>
          )}
          {params.row.status === 'scheduled' && (
            <Tooltip title="Send Now">
              <IconButton
                size="small"
                onClick={() => {
                  // Handle send now
                  toast.info('Sending campaign...');
                }}
              >
                <SendIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    }
  ];

  const getStatsCards = () => {
    if (currentTab === 0) {
      return [
        { title: 'Total Contacts', value: contactStats.stats?.totalContacts || 0, icon: <ContactIcon />, color: 'primary' },
        { title: 'New Contacts', value: contactStats.stats?.newContacts || 0, icon: <AddIcon />, color: 'warning' },
        { title: 'Replied', value: contactStats.stats?.repliedContacts || 0, icon: <ReplyIcon />, color: 'success' },
        { title: 'Closed', value: contactStats.stats?.closedContacts || 0, icon: <CheckCircleIcon />, color: 'info' }
      ];
    } else if (currentTab === 1) {
      return [
        { title: 'Total Tickets', value: ticketStats.stats?.totalTickets || 0, icon: <SupportIcon />, color: 'primary' },
        { title: 'Open Tickets', value: ticketStats.stats?.openTickets || 0, icon: <WarningIcon />, color: 'warning' },
        { title: 'In Progress', value: ticketStats.stats?.inProgressTickets || 0, icon: <TimeIcon />, color: 'info' },
        { title: 'Resolved', value: ticketStats.stats?.resolvedTickets || 0, icon: <CheckCircleIcon />, color: 'success' }
      ];
    } else if (currentTab === 2) {
      return [
        { title: 'Total Campaigns', value: campaignStats.stats?.totalCampaigns || 0, icon: <CampaignIcon />, color: 'primary' },
        { title: 'Draft', value: campaignStats.stats?.draftCampaigns || 0, icon: <EditIcon />, color: 'warning' },
        { title: 'Sent', value: campaignStats.stats?.sentCampaigns || 0, icon: <SendIcon />, color: 'success' },
        { title: 'Total Recipients', value: campaignStats.stats?.totalRecipients || 0, icon: <PeopleIcon />, color: 'info' }
      ];
    }
    return [];
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Communication & Support Tools
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {getStatsCards().map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Box sx={{ color: `${stat.color}.main`, mr: 2 }}>
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6">{stat.value}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
            <Tab 
              label={
                <Badge badgeContent={contactStats.stats?.newContacts || 0} color="warning">
                  Contact Forms
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={ticketStats.stats?.openTickets || 0} color="error">
                  Support Tickets
                </Badge>
              } 
            />
            <Tab label="Email Campaigns" />
          </Tabs>
        </Box>
        
        <CardContent>
          {currentTab === 0 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">Contact Form Submissions</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    // Handle create new contact
                    toast.info('Create contact functionality');
                  }}
                >
                  New Contact
                </Button>
              </Box>
              
              <DataGrid
                rows={contacts}
                columns={contactColumns}
                loading={loading}
                getRowId={(row) => row._id}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                checkboxSelection={false}
                disableSelectionOnClick
                autoHeight
              />
            </Box>
          )}

          {currentTab === 1 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">Support Tickets</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    // Handle create new ticket
                    toast.info('Create ticket functionality');
                  }}
                >
                  New Ticket
                </Button>
              </Box>
              
              <DataGrid
                rows={tickets}
                columns={ticketColumns}
                loading={loading}
                getRowId={(row) => row._id}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                checkboxSelection={false}
                disableSelectionOnClick
                autoHeight
              />
            </Box>
          )}

          {currentTab === 2 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">Email Campaigns</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    // Handle create new campaign
                    toast.info('Create campaign functionality');
                  }}
                >
                  New Campaign
                </Button>
              </Box>
              
              <DataGrid
                rows={campaigns}
                columns={campaignColumns}
                loading={loading}
                getRowId={(row) => row._id}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                checkboxSelection={false}
                disableSelectionOnClick
                autoHeight
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Contact Details Dialog */}
      <Dialog open={contactDialogOpen} onClose={() => setContactDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Contact Details</DialogTitle>
        <DialogContent>
          {selectedContact && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Contact Information</Typography>
                  <Typography variant="body1"><strong>Name:</strong> {selectedContact.name}</Typography>
                  <Typography variant="body1"><strong>Email:</strong> {selectedContact.email}</Typography>
                  <Typography variant="body1"><strong>Phone:</strong> {selectedContact.phone || 'N/A'}</Typography>
                  <Typography variant="body1"><strong>Subject:</strong> {selectedContact.subject}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Status & Priority</Typography>
                  <Box display="flex" gap={1} mb={2}>
                    <Chip label={selectedContact.status} color={statusColors[selectedContact.status]} />
                    <Chip label={selectedContact.priority} color={priorityColors[selectedContact.priority]} />
                  </Box>
                  <Typography variant="body1"><strong>Category:</strong> {selectedContact.category}</Typography>
                  <Typography variant="body1"><strong>Date:</strong> {new Date(selectedContact.createdAt).toLocaleString("en-IN", {
                    timeZone: "Asia/Kolkata",
                    dateStyle: "short",
                    timeStyle: "short"
                  })}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>Message</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body1">{selectedContact.message}</Typography>
                  </Paper>
                </Grid>
                {selectedContact.replies && selectedContact.replies.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Replies</Typography>
                    {selectedContact.replies.map((reply, index) => (
                      <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="body1">{reply.message}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          By {reply.repliedBy?.name} on {new Date(reply.repliedAt).toLocaleString("en-IN", {
                            timeZone: "Asia/Kolkata",
                            dateStyle: "short",
                            timeStyle: "short"
                          })}
                        </Typography>
                      </Paper>
                    ))}
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)}>Close</Button>
          <Button
            onClick={() => {
              setContactDialogOpen(false);
              setReplyDialogOpen(true);
            }}
            variant="contained"
            startIcon={<ReplyIcon />}
          >
            Reply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={replyDialogOpen} onClose={() => setReplyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reply to Contact</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write your reply..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplyDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReplyToContact} variant="contained">
            Send Reply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ticket Details Dialog */}
      <Dialog open={ticketDialogOpen} onClose={() => setTicketDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Ticket Details - {selectedTicket?.ticketNumber}</DialogTitle>
        <DialogContent>
          {selectedTicket && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Customer Information</Typography>
                  <Typography variant="body1"><strong>Name:</strong> {selectedTicket.customerName}</Typography>
                  <Typography variant="body1"><strong>Email:</strong> {selectedTicket.customerEmail}</Typography>
                  <Typography variant="body1"><strong>Subject:</strong> {selectedTicket.subject}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Ticket Details</Typography>
                  <Box display="flex" gap={1} mb={2}>
                    <Chip label={selectedTicket.status} color={statusColors[selectedTicket.status]} />
                    <Chip label={selectedTicket.priority} color={priorityColors[selectedTicket.priority]} />
                  </Box>
                  <Typography variant="body1"><strong>Category:</strong> {selectedTicket.category}</Typography>
                  <Typography variant="body1"><strong>Assigned To:</strong> {selectedTicket.assignedTo?.name || 'Unassigned'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>Description</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body1">{selectedTicket.description}</Typography>
                  </Paper>
                </Grid>
                {selectedTicket.messages && selectedTicket.messages.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Messages</Typography>
                    {selectedTicket.messages.map((message, index) => (
                      <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="body1">{message.message}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {message.sender === 'customer' ? 'Customer' : 'Agent'} - {new Date(message.createdAt).toLocaleString("en-IN", {
                            timeZone: "Asia/Kolkata",
                            dateStyle: "short",
                            timeStyle: "short"
                          })}
                        </Typography>
                      </Paper>
                    ))}
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTicketDialogOpen(false)}>Close</Button>
          <Button
            onClick={() => {
              setTicketDialogOpen(false);
              setMessageDialogOpen(true);
            }}
            variant="contained"
            startIcon={<MessageIcon />}
          >
            Add Message
          </Button>
        </DialogActions>
      </Dialog>

      {/* Message Dialog */}
      <Dialog open={messageDialogOpen} onClose={() => setMessageDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Message to Ticket</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Write your message..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessageDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddTicketMessage} variant="contained">
            Add Message
          </Button>
        </DialogActions>
      </Dialog>

      {/* Campaign Details Dialog */}
      <Dialog open={campaignDialogOpen} onClose={() => setCampaignDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Campaign Details</DialogTitle>
        <DialogContent>
          {selectedCampaign && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Campaign Information</Typography>
                  <Typography variant="body1"><strong>Name:</strong> {selectedCampaign.name}</Typography>
                  <Typography variant="body1"><strong>Subject:</strong> {selectedCampaign.subject}</Typography>
                  <Typography variant="body1"><strong>Type:</strong> {selectedCampaign.type}</Typography>
                  <Typography variant="body1"><strong>Template:</strong> {selectedCampaign.template}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Status & Statistics</Typography>
                  <Box display="flex" gap={1} mb={2}>
                    <Chip label={selectedCampaign.status} color={statusColors[selectedCampaign.status]} />
                  </Box>
                  <Typography variant="body1"><strong>Recipients:</strong> {selectedCampaign.statistics?.totalRecipients || 0}</Typography>
                  <Typography variant="body1"><strong>Sent:</strong> {selectedCampaign.statistics?.sent || 0}</Typography>
                  <Typography variant="body1"><strong>Opened:</strong> {selectedCampaign.statistics?.opened || 0}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>Content Preview</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 200, overflow: 'auto' }}>
                    <div dangerouslySetInnerHTML={{ __html: selectedCampaign.content }} />
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignDialogOpen(false)}>Close</Button>
          {selectedCampaign?.status === 'draft' && (
            <Button
              onClick={() => {
                setCampaignDialogOpen(false);
                setScheduleDialogOpen(true);
              }}
              variant="contained"
              startIcon={<ScheduleIcon />}
            >
              Schedule
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule Campaign</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="datetime-local"
            label="Schedule Date & Time"
            value={scheduleDate}
            onChange={(e) => setScheduleDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleScheduleCampaign} variant="contained">
            Schedule Campaign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommunicationSupport;
